import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageTemplate from '../components/PageTemplate';

const SupportPage = () => {
  const [ticketData, setTicketData] = useState({
    name: '',
    email: '',
    subject: '',
    category: '',
    priority: 'medium',
    description: '',
    attachFile: false
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTicketData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when field is changed
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!ticketData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!ticketData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(ticketData.email)) {
      errors.email = 'Invalid email address';
    }

    if (!ticketData.subject.trim()) {
      errors.subject = 'Subject is required';
    }

    if (!ticketData.category) {
      errors.category = 'Please select a category';
    }

    if (!ticketData.description.trim()) {
      errors.description = 'Description is required';
    } else if (ticketData.description.trim().length < 20) {
      errors.description = 'Description must be at least 20 characters';
    }

    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const errors = validateForm();
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      setIsSubmitting(true);

      // Simulate API call
      setTimeout(() => {
        console.log('Support ticket submitted:', ticketData);
        setIsSubmitting(false);
        setSubmitSuccess(true);

        // Reset form after successful submission
        setTicketData({
          name: '',
          email: '',
          subject: '',
          category: '',
          priority: 'medium',
          description: '',
          attachFile: false
        });

        // Reset success message after 5 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      }, 1500);
    }
  };

  const supportCategories = [
    { id: 'account', title: 'Account Issues', icon: '👤' },
    { id: 'technical', title: 'Technical Problems', icon: '🔧' },
    { id: 'billing', title: 'Billing & Payments', icon: '💳' },
    { id: 'feature', title: 'Feature Requests', icon: '💡' },
    { id: 'bug', title: 'Bug Reports', icon: '🐞' },
    { id: 'other', title: 'Other', icon: '❓' }
  ];

  const supportResources = [
    {
      title: 'Help Center',
      description: 'Browse our comprehensive knowledge base for answers to common questions.',
      icon: '📚',
      link: '/help'
    },
    {
      title: 'User Guides',
      description: 'Step-by-step guides to help you get the most out of Patients Empowered Coach and Patients Empowered Practice.',
      icon: '📖',
      link: '/guides'
    },
    {
      title: 'FAQ',
      description: 'Find answers to frequently asked questions about our platform.',
      icon: '❓',
      link: '/faq'
    },
    {
      title: 'Video Tutorials',
      description: 'Watch video demonstrations of Patients Empowered features and functions for both patients and providers.',
      icon: '🎬',
      link: '/tutorials'
    }
  ];

  return (
    <PageTemplate title="Support">
      <div className="grid grid-cols-1 grid-lg-cols-3 gap-lg">
        <div className="grid-lg-cols-2">
          <section className="mb-5">
            <h2 className="mb-4">Submit a Support Ticket</h2>

            {submitSuccess && (
              <div className="alert alert-success mb-4">
                <p className="mb-0">Your support ticket has been submitted successfully! Our team will get back to you soon.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="card">
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Your Name <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={ticketData.name}
                  onChange={handleChange}
                  className={`form-control ${formErrors.name ? 'error' : ''}`}
                />
                {formErrors.name && <div className="form-error">{formErrors.name}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email Address <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={ticketData.email}
                  onChange={handleChange}
                  className={`form-control ${formErrors.email ? 'error' : ''}`}
                />
                {formErrors.email && <div className="form-error">{formErrors.email}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="subject" className="form-label">
                  Subject <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={ticketData.subject}
                  onChange={handleChange}
                  className={`form-control ${formErrors.subject ? 'error' : ''}`}
                />
                {formErrors.subject && <div className="form-error">{formErrors.subject}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="category" className="form-label">
                  Category <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <select
                  id="category"
                  name="category"
                  value={ticketData.category}
                  onChange={handleChange}
                  className={`form-control form-select ${formErrors.category ? 'error' : ''}`}
                >
                  <option value="">Select a category</option>
                  {supportCategories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.title}
                    </option>
                  ))}
                </select>
                {formErrors.category && <div className="form-error">{formErrors.category}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="priority" className="form-label">Priority</label>
                <select
                  id="priority"
                  name="priority"
                  value={ticketData.priority}
                  onChange={handleChange}
                  className="form-control form-select"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="description" className="form-label">
                  Description <span style={{ color: '#e74c3c' }}>*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={ticketData.description}
                  onChange={handleChange}
                  className={`form-control form-textarea ${formErrors.description ? 'error' : ''}`}
                  rows="5"
                  placeholder="Please describe your issue in detail. Include any error messages you've received and steps to reproduce the problem."
                />
                {formErrors.description && <div className="form-error">{formErrors.description}</div>}
              </div>

              <div className="form-group">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="attachFile"
                    name="attachFile"
                    checked={ticketData.attachFile}
                    onChange={handleChange}
                    style={{ marginRight: '10px' }}
                  />
                  <label htmlFor="attachFile" className="mb-0">
                    I want to attach a file (screenshot, document, etc.)
                  </label>
                </div>
              </div>

              {ticketData.attachFile && (
                <div className="form-group">
                  <input
                    type="file"
                    id="file"
                    name="file"
                    className="form-control"
                  />
                  <div className="form-help">Maximum file size: 10MB. Supported formats: JPG, PNG, PDF, DOC, DOCX.</div>
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Ticket'}
              </button>
            </form>
          </section>
        </div>

        <div>
          <section className="mb-5">
            <h2 className="mb-4">Support Resources</h2>
            <div className="grid grid-cols-1 gap-md">
              {supportResources.map((resource, index) => (
                <div key={index} className="card">
                  <div className="flex gap-md">
                    <div className="text-center" style={{ fontSize: '2rem', minWidth: '60px' }}>
                      {resource.icon}
                    </div>
                    <div>
                      <h3 className="mb-1">{resource.title}</h3>
                      <p className="mb-3">{resource.description}</p>
                      <Link to={resource.link} className="btn btn-secondary btn-sm">View Resource</Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="mb-4">Contact Information</h2>
            <div className="card">
              <h3 className="mb-3">Support Hours</h3>
              <p className="mb-3">Our support team is available during the following hours:</p>
              <ul className="mb-4">
                <li className="mb-1"><strong>Monday-Friday:</strong> 9:00 AM - 8:00 PM EST</li>
                <li className="mb-1"><strong>Saturday:</strong> 10:00 AM - 6:00 PM EST</li>
                <li><strong>Sunday:</strong> Closed</li>
              </ul>

              <h3 className="mb-3">Contact</h3>
              <p className="mb-0">For support inquiries, please use the form above.</p>
            </div>
          </section>
        </div>
      </div>
    </PageTemplate>
  );
};

export default SupportPage;
